<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <teleport to="#app">
    <oxd-dialog
      v-if="show"
      class="orangehrm-buzz-mention-dialog"
      @update:show="onClose"
    >
      <oxd-loading-spinner v-if="isLoading" />
      <summary-card
        v-else-if="employee"
        embedded
        :employee-id="employee.id"
        :employee-name="employee.employeeName"
        :employee-nickname="employee.employeeNickname"
        :show-nickname-under-title="true"
        :employee-designation="employee.employeeDesignation"
        :employee-sub-unit="employee.employeeSubUnit"
        :employee-location="employee.employeeLocation"
      >
        <employee-details
          :employee-id="employee.id"
          api-path="/api/v2/buzz/employees"
        />
        <template v-if="assignedPic">
          <div class="orangehrm-buzz-mention-dialog-pic">
            <oxd-text type="toast-message">
              {{ $t('leave.assigned_pic') }}
            </oxd-text>
            <oxd-text type="toast-title">
              {{ assignedPicName }}
            </oxd-text>
          </div>
        </template>
      </summary-card>
      <oxd-text v-else tag="p" class="orangehrm-buzz-mention-dialog-error">
        {{ loadError || $t('general.unexpected_error') }}
      </oxd-text>
    </oxd-dialog>
  </teleport>
</template>

<script>
import {ref, watch, computed} from 'vue';
import {OxdDialog, OxdSpinner, OxdText} from '@ohrm/oxd';
import usei18n from '@/core/util/composable/usei18n';
import {APIService} from '@/core/util/services/api.service';
import SummaryCard from '@/orangehrmCorporateDirectoryPlugin/components/SummaryCard';
import EmployeeDetails from '@/orangehrmCorporateDirectoryPlugin/components/EmployeeDetails';
import useBuzzEmployeeMentionDialog from '@/orangehrmBuzzPlugin/util/composable/useBuzzEmployeeMentionDialog';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import {resolveEmployeeNickname} from '@/core/util/helper/employeeDisplayName';

export default {
  name: 'BuzzEmployeeMentionDialog',

  components: {
    'oxd-dialog': OxdDialog,
    'oxd-loading-spinner': OxdSpinner,
    'oxd-text': OxdText,
    'summary-card': SummaryCard,
    'employee-details': EmployeeDetails,
  },

  setup() {
    const {$t} = usei18n();
    const {$tEmpName} = useEmployeeNameTranslate();
    const {show, empNumber, assignedPic, closeEmployeeMention} =
      useBuzzEmployeeMentionDialog();
    const isLoading = ref(false);
    const employee = ref(null);
    const loadError = ref('');
    let loadRequestId = 0;
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/buzz/employees',
    );

    const normalizeEmployee = (item) => {
      const nickname = resolveEmployeeNickname(item);

      return {
        id: item.empNumber,
        employeeName: $tEmpName(item, {
          includeMiddle: true,
          excludePastEmpTag: false,
        }),
        employeeNickname: nickname || '',
        employeeDesignation: item.jobTitle?.isDeleted
          ? `${item.jobTitle?.title} ${$t('general.deleted')}`
          : item.jobTitle?.title,
        employeeSubUnit: item.subunit?.name ?? '',
        employeeLocation: item.location?.name ?? '',
      };
    };

    const loadEmployee = (empNum) => {
      if (!empNum) {
        employee.value = null;
        loadError.value = '';
        return;
      }

      const requestId = ++loadRequestId;
      isLoading.value = true;
      employee.value = null;
      loadError.value = '';
      http
        .get(empNum, {model: 'detailed'})
        .then(({data}) => {
          if (requestId !== loadRequestId) {
            return;
          }
          employee.value = normalizeEmployee(data.data);
        })
        .catch((error) => {
          if (requestId !== loadRequestId) {
            return;
          }
          loadError.value =
            error?.response?.data?.error?.message ||
            error?.message ||
            $t('general.unexpected_error');
        })
        .finally(() => {
          if (requestId === loadRequestId) {
            isLoading.value = false;
          }
        });
    };

    watch([show, empNumber], ([visible, id]) => {
      if (visible && id) {
        loadEmployee(id);
        return;
      }
      if (!visible) {
        loadRequestId += 1;
        employee.value = null;
        loadError.value = '';
        isLoading.value = false;
      }
    });

    const onClose = () => {
      closeEmployeeMention();
    };

    const assignedPicName = computed(() => {
      const pic = assignedPic.value;
      if (!pic) {
        return '';
      }
      return $tEmpName(pic, {
        includeMiddle: true,
        excludePastEmpTag: false,
      });
    });

    return {
      show,
      employee,
      assignedPic,
      assignedPicName,
      isLoading,
      loadError,
      onClose,
    };
  },
};
</script>

<style src="./buzz-employee-mention-dialog.scss" lang="scss" scoped></style>
